import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Sparkles } from "lucide-react";
import { StarRating } from "@/components/site/StarRating";

const SOURCES = ["WhatsApp", "Instagram", "Facebook", "Google", "Email", "In person"];
const EMPTY = { trip_id: "", name: "", city: "", rating: 5, text: "", travelled_on: "", source: "WhatsApp", featured: false, active: true };

export default function ReviewsManage() {
  const [reviews, setReviews] = useState([]);
  const [trips, setTrips] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/reviews").then(({ data }) => setReviews(data));
  useEffect(() => { load(); api.get("/admin/trips").then(({ data }) => setTrips(data)); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.trip_id) { toast.error("Select a trip"); return; }
    if (!form.name.trim() || !form.text.trim()) { toast.error("Name and review text are required"); return; }
    const payload = { ...form, rating: Number(form.rating) };
    try {
      if (editing) await api.put(`/admin/reviews/${editing.id}`, payload); else await api.post("/admin/reviews", payload);
      toast.success("Review saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (r) => { await api.delete(`/admin/reviews/${r.id}`); toast.success("Deleted"); load(); };
  const toggle = async (r, k) => {
    const { id, trip_name, trip_slug, created_at, updated_at, ...rest } = r;
    await api.put(`/admin/reviews/${id}`, { ...rest, [k]: !r[k] }); load();
  };

  const shown = filter === "all" ? reviews : reviews.filter((r) => r.trip_id === filter);
  const avg = shown.length ? (shown.reduce((a, r) => a + r.rating, 0) / shown.length).toFixed(1) : "—";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-heading text-4xl font-semibold">Reviews</h1><p className="text-muted-foreground">Add traveller feedback from WhatsApp / Instagram to build trust on trip pages.</p></div>
        <Button className="rounded-full" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="add-review"><Plus className="h-4 w-4 mr-1" /> Add Review</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-64" data-testid="review-filter-trip"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All trips</SelectItem>{trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm" data-testid="review-stats">
          <StarRating value={Number(avg) || 0} /> <span className="font-medium">{avg}</span> · {shown.length} review{shown.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {shown.map((r) => (
          <div key={r.id} className={`rounded-xl border bg-card p-5 flex flex-col ${!r.active ? "opacity-60" : ""}`} data-testid="admin-review-card">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <StarRating value={r.rating} />
                <p className="font-medium mt-1 truncate">{r.name}{r.city ? <span className="text-muted-foreground font-normal"> · {r.city}</span> : null}</p>
                <p className="text-xs text-primary truncate">{r.trip_name}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); }} data-testid="edit-review"><Pencil className="h-4 w-4" /></Button>
                <AlertDialog><AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive" data-testid="delete-review"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                  <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete review by {r.name}?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(r)} className="bg-destructive" data-testid="confirm-delete-review">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
              </div>
            </div>
            <p className="text-sm text-foreground/80 mt-3 flex-1 line-clamp-4">{r.text}</p>
            <div className="mt-4 flex items-center justify-between text-xs">
              <Badge variant="outline" className="rounded-full">{r.source}</Badge>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 cursor-pointer"><Switch checked={r.featured} onCheckedChange={() => toggle(r, "featured")} data-testid="toggle-featured" /><Sparkles className="h-3.5 w-3.5 text-gold" /> Home</label>
                <label className="flex items-center gap-1.5 cursor-pointer"><Switch checked={r.active} onCheckedChange={() => toggle(r, "active")} data-testid="toggle-active" /> Live</label>
              </div>
            </div>
          </div>
        ))}
        {!shown.length && <p className="text-muted-foreground col-span-full py-10 text-center">No reviews yet. Add your first one.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Edit" : "Add"} Review</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Trip</Label>
              <Select value={form.trip_id} onValueChange={(v) => set("trip_id", v)}>
                <SelectTrigger data-testid="review-trip"><SelectValue placeholder="Select trip" /></SelectTrigger>
                <SelectContent>{trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Traveller name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="review-name" /></div>
              <div><Label>City</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="review-city" /></div>
            </div>
            <div><Label>Rating</Label><div className="mt-1"><StarRating value={form.rating} size="h-7 w-7" interactive onChange={(n) => set("rating", n)} /></div></div>
            <div><Label>Review</Label><Textarea rows={4} value={form.text} onChange={(e) => set("text", e.target.value)} placeholder="Paste feedback from WhatsApp / Instagram…" data-testid="review-text" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Travelled on</Label><Input value={form.travelled_on} onChange={(e) => set("travelled_on", e.target.value)} placeholder="e.g. May 2026" data-testid="review-travelled" /></div>
              <div><Label>Source</Label>
                <Select value={form.source} onValueChange={(v) => set("source", v)}>
                  <SelectTrigger data-testid="review-source"><SelectValue /></SelectTrigger>
                  <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="review-featured" /> Show on Home</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="review-active" /> Live</label>
            </div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-review">Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
